import * as Sentry from '@sentry/browser';

import { TrackingService, TrackingInfo, User, DISPLAY_MODE } from '../tracking.types';
export { DISPLAY_MODE };
import { TrackingName } from '../tracking.constant';
import mixpanel from 'mixpanel-browser';
import { generateUUIDFromStringKey } from '@core/services/orm/idGenerator';
import Env from "@env";

export function getPWADisplayMode(): DISPLAY_MODE {
    const isTWA = sessionStorage.getItem('twa') === 'true';
    if (isTWA)
        return DISPLAY_MODE.TWA;
  if (window.matchMedia('(display-mode: browser)').matches)
    return DISPLAY_MODE.BROWSER;
  if (window.matchMedia('(display-mode: standalone)').matches)
    return DISPLAY_MODE.STANDALONE;
  if (window.matchMedia('(display-mode: minimal-ui)').matches)
    return DISPLAY_MODE.MINIMAL_UI;
  if (window.matchMedia('(display-mode: fullscreen)').matches)
    return DISPLAY_MODE.FULLSCREEN;
  if (window.matchMedia('(display-mode: window-controls-overlay)').matches)
    return DISPLAY_MODE.WINDOW_CONTROLS_OVERLAY;

  return DISPLAY_MODE.UNKNOWN;
}

class TrackingServiceImpl implements TrackingService {

    protected user?: User | null;
    // mixpanel.init() is a no-op when no token is provided, leaving the client unusable
    protected readonly enabled: boolean;

    constructor(
        protected mixpanelClient = mixpanel
    ) {
        this.enabled = Boolean(Env["@archipad/tracking-service"]?.projectToken);
        if (this.enabled) {
            this.mixpanelClient.init(Env["@archipad/tracking-service"]?.projectToken,
                {
                    api_host: Env["@archipad/tracking-service"]?.endpoint,
                }
            );
        }
    }

    /**
     * Mixpanel events are not sent when an admin is "logged as" another user:
     * they would be attributed to that user and pollute their activity.
     */
    private get mixpanelEnabled(): boolean {
        return this.enabled && !this.user?.logAs;
    }

    initAction({ name }: Pick<TrackingInfo, 'name'>): void {
        if (!this.mixpanelEnabled) {
            return;
        }
        this.mixpanelClient.time_event(name);
    }
    /**
      * Model,  Add / Remove / RemoveMulti / Close, Bug/Map/WP/PlanningEvent/..., selectionCount?
      * Account, Signup/Login/Logout
      * Upgrade, ShowFromMenu/ShowFromInteraction/DismissedInfo/ShowPayment/ShowContact/DismissedPayment/DismissedContact/PaymentDone/ContactDone
      * View, ShowLeftPanel/ShowList/ShowMap,
      * ShareProject, SetRight/InviteConfirm/InviteCancel/InvitePay/PaymentCancel, admin/none/...
      * Sync, Publish/PublishMulti/FetchBackground/TempPatchPushBackground/TempPatchConflict/TempPatchResolveUsingMine/TempPatchResolveUsingTheirs/Rebase/RebaseClean
     */
    trackAction(trackingInfo: TrackingInfo): void {
        const { category, name: actionName, label } = trackingInfo;

        if (this.mixpanelEnabled && Env["@archipad/tracking-service"]?.allowListEvent?.includes(actionName)) {
            if (trackingInfo.unique_id){
                this.trackUniqueActionBaseOnUserId(trackingInfo);
            } else {
                this.mixpanelClient.track(actionName, trackingInfo);
            }
        }
        if ( category == "Account" && actionName == 'Register' ) {
            gtag("event", "sign_up", {
                method: "email",
            });
        }
        if ( category == "Account" && actionName == 'Delete' && label == 'DeletionSuccess' ) {
            gtag("event", "unsubscribe");
        }
    }


    private trackUniqueActionBaseOnUserId(trackingInfo: TrackingInfo): void {
        if (!this.mixpanelEnabled || !this.user) {
            return;
        }
        const { name: actionName } = trackingInfo;
        const userId = this.user.authId ?? this.user.userId.toString();
        const trackingKey = `tracking-${actionName}-${userId}`;
        const alreadyTracked = localStorage.getItem(trackingKey);
        const uniqueIdByUser = `${userId}-${trackingInfo.unique_id}`;
        if (alreadyTracked === uniqueIdByUser) {
            return;
        }
        this.mixpanelClient.track(actionName, {
            ...trackingInfo,
            $insert_id: generateUUIDFromStringKey(uniqueIdByUser),
        });
        localStorage.setItem(trackingKey, uniqueIdByUser);
    }

    setUser(user: User): void {
        this.user = user;
        const displayMode = getPWADisplayMode();
        try {
            if (user !== null) {
                gtag('set', {'user_id': user.userId.toString()});
                Sentry.setUser({
                    id: user.authId,
                    cloudUserId: user.userId.toString(),
                    username: user.login,
                    email: user.login,
                    isRoot: user.isRoot,
                    displayMode: displayMode,
                });
                // Mixpanel identities are keyed on the auth server id only. Callers that
                // only know the cloud user id (user session subscription, cache restore,
                // legacy login) must not create a second, numeric profile for the same person.
                if (!user.authId) {
                    return;
                }
                if (this.mixpanelEnabled) {
                    const distinctId = user.authId;
                    // Mixpanel links a $device_id to a single $user_id. Identifying another
                    // user on the same device is rejected (errAnonDistinctIdAssignedAlready),
                    // so start from a fresh anonymous identity when the user changes.
                    const previousDistinctId: unknown = this.mixpanelClient.get_property('$user_id');
                    if (previousDistinctId && previousDistinctId !== distinctId) {
                        this.mixpanelClient.reset();
                    }
                    this.mixpanelClient.identify(distinctId);
                    this.mixpanelClient.people.set({
                        $name: user.login,
                        // Reserved property shown by Mixpanel; `email` is kept for existing reports.
                        $email: user.login,
                        email: user.login,
                        isRoot: user.isRoot,
                        cloudUserId: user.userId.toString(),
                        displayMode: displayMode,
                    });
                }
                const dailyActivityInsertId = `${new Date().toISOString().slice(0,10)}-${user.authId}`;
                this.trackAction({
                    name: TrackingName.daily_activity,
                    unique_id: dailyActivityInsertId,
                    displayMode: displayMode,
                });
            } else {
                gtag('set', {'user_id': null});
                Sentry.setUser(null);
                if (this.enabled) {
                    this.mixpanelClient.reset();
                }
            }
        } catch {
            //Ignore all errors
        }

    }

    /**
     * Tracking used in loadTask
     */
    trackTask(trackingInfo: TrackingInfo, state: "begin" | "end" | "error", error: Error): void {
        const { category, name: actionName } = trackingInfo;
        if (state !== "end") {
            return;
        }

        if (category === "Account" && actionName === "Login") {
            gtag("event", "login", {
                method: "email",
            })
        }
        if (category === "Account" && actionName === "Register") {
            gtag("event", "sign_up", {
                method: "email",
            })
        }
    }

    getPWADisplayMode(): DISPLAY_MODE {
        return getPWADisplayMode();
    }


}

export {
    TrackingName,
}
export default new TrackingServiceImpl();
